import { combineEpics, ofType, StateObservable } from 'redux-observable';
import { iif, Observable, of } from 'rxjs';
import { delay, map, mergeMap, switchMap, take, throttleTime } from 'rxjs/operators';
import { OrderedMap, Set } from 'immutable';
import Contact from '@models/Contact';
import { AppState } from '@reducers/rootReducer';
import { ModalType } from '@reducers/ui/modal';
import { ID, IDRecord, ReduxAction } from '@app/types';
import Note from '@models/Note';
import { setCurrentlyEditingNote } from './notes';
import { batchActions } from 'redux-batched-actions';
import { ContextMenuState } from '@reducers/ui/contextMenu';
import { createSelector } from 'reselect';
import { selectActiveGroupView } from './groupView';
import { actionTypes, selectSearch } from './search';
import { AppView } from '@reducers/ui/location';
import { AnyViewState, ViewID } from '@reducers/ui/viewRestoration';
import { SettingsNavState } from '@views/Settings/panels';
import { selectContact, selectContactGetter } from './contacts';
import { isPersistedID } from '@support/id-check';
import { selectExpandableActivityItem } from '@reducers/data/expandableActivityItems';
import { setCSSVariable } from '@app/themes';

export interface AppLocation {
  pathname: string;
}

// verbiage: selected vs. highlighted vs. focused vs. active

/* Selectors */
const viewHasMultiselect = (view: AppView) =>
  ['Search', 'People'].includes(view) || view?.startsWith('Group');
export const selectCurrentView = (state: AppState) => state.ui.location.view;
export const selectIsMultiselectView = createSelector(selectCurrentView, viewHasMultiselect);

export const selectHighlightedContact = (state: AppState) => {
  const fallbackHighlightedContact = state.ui.selected.contact;
  const highlightedContact = selectContact(fallbackHighlightedContact?.id)(state);
  const contact = highlightedContact || fallbackHighlightedContact;
  return contact && contact.isPersisted ? contact : undefined;
};
export const selectSelectedContactBatch = (state: AppState): Set<ID> =>
  state.ui.selected.contactBatch;

export const selectArrayOfSelectedContacts = (state: AppState): Array<Contact> => {
  const selectedContactBatch = selectSelectedContactBatch(state);
  const getContact = selectContactGetter(state);

  if (selectedContactBatch.size == 0) {
    return [];
  }

  const view = selectCurrentView(state);
  let idMap = OrderedMap<ID, IDRecord>();

  if (view.startsWith('Group')) {
    idMap = selectActiveGroupView(state).group?.items || OrderedMap();
  } else if (view == 'Search') {
    idMap = selectSearch(state).results.items;
  } else if (view == 'People') {
    idMap = state.data.allPeopleSearch.results.items;
  }

  return idMap
    .map((_, id) => getContact(id))
    .filter((contact, id) => contact && selectedContactBatch.has(id))
    .valueSeq()
    .toArray();
};

// This is either the only selected (checked) contact or the highlighted contact
export const selectSelectedContact = (state: AppState) => {
  const highlightedContact = selectHighlightedContact(state);
  const selectedContactBatch = selectSelectedContactBatch(state);
  const view = selectCurrentView(state);

  // if we're in a multiselect view and there is one selected contact, return that contact
  if (view && viewHasMultiselect(view) && selectedContactBatch.size == 1) {
    const onlySelectedContactID = String(selectedContactBatch.first());
    // const contactInGroup = state.ui.groupView?.group?.contactsMap?.get(onlySelectedContactID);
    return state.data.contacts.collection.items.get(onlySelectedContactID);
  }

  return highlightedContact;
};

export const selectSavedSelectedContactForView = (pathname: string) => (state: AppState) => {
  const savedSelectionState = state.ui.selected.savedContactSelection;
  if (savedSelectionState?.pathname == pathname) {
    return savedSelectionState.contact;
  }
  return null;
};

export const selectScrollPosition = (viewID: string) => (state: AppState) =>
  state.ui.scrollPosition.get(viewID);

export const selectCurrentLocation = (state: AppState) => state.ui.location;

export const selectSavedViewState = (viewID: ViewID) => (state: AppState) =>
  state.ui.viewRestoration.get(viewID);
export const selectLastPathname = (state: AppState) => state.ui.location.lastPathname;
export const selectPageSource = (state: AppState) => state.ui.location.pageSource;
export const modalStateSelector = (state: AppState) => state.ui.modal;
export const selectContextMenu = (state: AppState) => state.ui.contextMenu;
export const selectRedirectPathname = (state: AppState) => state.ui.location.redirect;
export const selectActiveModalType = (state: AppState): ModalType =>
  state.ui.modal.isOpen ? state.ui.modal.modalType : null;
export const selectActiveModalDispatchOrigin = (state: AppState) => state.ui.modal.dispatchOrigin;
export const selectSettingsNav = (state: AppState) => state.ui.modal.settingsNav;
export const selectActivityItemInModal = (state: AppState) =>
  selectExpandableActivityItem(state.ui.modal.activityItemID)(state);

export const contactAndNoteModalStateSelector = (state: AppState) => {
  const modalState = state.ui.modal.contactAndNoteModal;

  let contact = modalState.contact;
  if (isPersistedID(contact?.id)) {
    const cachedContact = selectContact(String(contact.id), undefined)(state);
    contact = cachedContact || contact;
  }

  return { ...modalState, contact };
};

export const selectIsModalOpen = (state: AppState) => {
  const { isOpen, settingsNav } = state.ui.modal;
  return isOpen || !!settingsNav;
};

export const creatingNewNoteInContactViewSelector = (state: AppState) =>
  state.ui.contactView.creatingNewNote;

export const selectNewReminderEditorOpenInContactView = (state: AppState) =>
  state.ui.contactView.newReminderEditorOpen;

/* Actions */
export const setLocation = (location: AppLocation) => ({
  type: 'SET_LOCATION',
  payload: location,
});

export const setActiveContact = (contact: Contact | null, contactEventSource?: string) => ({
  type: 'SELECT_CONTACT',
  payload: { contact, contactEventSource },
});

export const setContactBatch = (contactBatch: Set<ID>, contactBatchEventSource?: string) => ({
  type: 'SELECT_CONTACT_BATCH',
  payload: { contactBatch, contactBatchEventSource },
});

export const clearContactBatch = () => ({
  type: 'SELECT_CONTACT_BATCH',
  payload: { contactBatch: null, contactBatchEventSource: null },
});

// For caching contact data that has been loaded from another endpoint (e.g. the /feed)
export const maybeCacheContact = (contact: Contact) => ({
  type: 'MAYBE_CACHE_CONTACT',
  payload: { contact },
});

export const cacheContacts = (contacts: Contact[]) => ({
  type: 'CACHE_CONTACTS',
  payload: { contacts },
});

export const pinNavBar = () => ({
  type: 'TRANSITION_NAV_BAR',
  payload: { status: 'pinned' },
});

export const hideNavBar = () => ({
  type: 'TRANSITION_NAV_BAR',
  payload: { status: 'hidden' },
});

export const popOutNavBar = () => ({
  type: 'TRANSITION_NAV_BAR',
  payload: { status: 'floating' },
});

export const resizeNavBar = (width: number) => ({
  type: 'RESIZE_NAV_BAR',
  payload: { width },
});

export const resizeRightSidebar = (width: number) => ({
  type: 'RESIZE_RIGHT_SIDEBAR',
  payload: { width },
});

export const addSourceLabel = (sourceType: 'email' | 'phone', label: string) => ({
  type: 'ADD_SOURCE_LABEL',
  payload: { sourceType, label },
});

export const removeSourceLabel = (sourceType: 'email' | 'phone', label: string) => ({
  type: 'REMOVE_SOURCE_LABEL',
  payload: { sourceType, label },
});

interface OpenModalOpts {
  source?: string;
  groupID?: ID;
}
export const openModal = (modalType: ModalType, opts?: OpenModalOpts) => ({
  type: 'OPEN_MODAL',
  payload: {
    type: modalType,
    ...(opts || {}),
  },
});

export const closeModal = { type: 'CLOSE_MODAL' };
export const closeModalAndClearNoteDraft = { type: 'CLOSE_MODAL_AND_CLEAR_NOTE_DRAFT' };

export const openContextMenu = (payload: Omit<ContextMenuState, 'isOpen'>) => ({
  type: 'OPEN_CONTEXT_MENU',
  payload,
});
export const closeContextMenu = { type: 'CLOSE_CONTEXT_MENU' };

export const startNewContact = (contact?: Contact) => ({
  type: 'START_NEW_CONTACT_IN_MODAL',
  payload: { contact },
});
// newNoteEpic will dispatch off this action based on the current state of the UI
export const startNewNote = { type: 'START_NEW_NOTE' };
export const startNewReminder = { type: 'START_NEW_REMINDER' };
export const setCreatingNewNoteInContactView = (
  creatingNewNote: boolean,
  creatingNewReminder?: boolean,
) => ({
  type: 'SET_CREATING_NEW_NOTE_IN_CONTACT_VIEW',
  payload: { creatingNewNote, creatingNewReminder },
});

export const openNewReminderEditorInContactView = {
  type: 'OPEN_NEW_REMINDER_EDITOR_IN_CONTACT_VIEW',
};
export const closeNewReminderEditorInContactView = {
  type: 'CLOSE_NEW_REMINDER_EDITOR_IN_CONTACT_VIEW',
};

export const startNewNoteInModal = (contact?: Contact, creatingNewReminder?: boolean) => ({
  type: 'START_NEW_NOTE_IN_MODAL',
  payload: { contact, creatingNewReminder },
});

// for editing an existing (persisted) note in a modal
export const openExistingNoteInModal = (payload: {
  contact?: Contact;
  note?: Note;
  focusMode?: boolean;
}) => ({
  type: 'OPEN_NOTE_IN_MODAL',
  payload,
});

export const updateContactAndNoteModal = (payload: {
  contact?: Partial<Contact>;
  note?: Note;
}) => ({
  type: 'UPDATE_CONTACT_AND_NOTE_MODAL',
  payload,
});

export const openActivityItemInModal = (activityItemID: ID) => ({
  type: 'OPEN_ACTIVITY_ITEM_IN_MODAL',
  payload: { activityItemID },
});

export const navigateToSettings = (payload?: SettingsNavState) => ({
  type: 'NAVIGATE_TO_SETTINGS',
  payload: { settingsNav: payload },
});

export const closeSettings = { type: 'CLOSE_SETTINGS' };

export const saveScrollPosition = (payload: { viewID: string; scrollPosition: number }) => ({
  type: 'SAVE_SCROLL_POSITION',
  payload,
});

export const resetScrollPosition = (viewID: string) => ({
  type: 'SAVE_SCROLL_POSITION',
  payload: { viewID, scrollPosition: 0 },
});

export const saveContactSelectionState = (payload: {
  pathname: string;
  selectedContact: Contact;
  selectedContactBatch: Set<ID> | null;
}) => ({
  type: 'SAVE_CONTACT_SELECTION_STATE',
  payload,
});

// This is specifically for performing navigation from outside of the router context (like in an epic
// or any component not wrapped by the AppRoutes). A root-level component (RoutesWrapper) listens
// to state.ui.location.redirect and navigate()s when there is a change
export const redirectLocation = (payload: { pathname: string }) => ({
  type: 'REDIRECT_LOCATION',
  payload,
});

export const saveViewState = (viewID: ViewID, state: AnyViewState) => ({
  type: 'SAVE_VIEW_STATE',
  payload: { viewID, state },
});

export const toggleWelcomeGuideVisibility = (visible: boolean) => ({
  type: 'TOGGLE_WELCOME_GUIDE_VISIBILITY',
  payload: visible,
});

// Side effects resulting from UI state changes
export const uiStateChangeSideEffects = (_store) => (next) => (action: ReduxAction) => {
  if (action.type === 'RESIZE_NAV_BAR') {
    setCSSVariable('nav-bar-width', `${action.payload.width}px`);
  }

  if (action.type === 'RESIZE_RIGHT_SIDEBAR') {
    setCSSVariable('right-sidebar-width', `${action.payload.width}px`);
  }

  if (action.type === 'LOAD_LOCAL_STORE_SUCCEEDED') {
    const savedNavBarWidth = action.payload.get('nav_bar_width');
    const savedRightSidebarWidth = action.payload.get('right_sidebar_width');

    if (savedNavBarWidth) {
      setCSSVariable('nav-bar-width', `${savedNavBarWidth}px`);
    }

    if (savedRightSidebarWidth) {
      setCSSVariable('right-sidebar-width', `${savedRightSidebarWidth}px`);
    }
  }

  return next(action);
};

export const newNoteEpic = (action$: Observable<ReduxAction>, state$: StateObservable<AppState>) =>
  action$.pipe(
    ofType('START_NEW_NOTE', 'START_NEW_REMINDER'),
    mergeMap((action) => {
      const contact = state$.value.ui.selected.contact;
      return iif(
        () => state$.value.ui.location.view == 'Contact - Detail',
        of(true).pipe(
          map(() =>
            batchActions([
              setCurrentlyEditingNote(new Note({ contact })),
              setCreatingNewNoteInContactView(true, action.type == 'START_NEW_REMINDER'),
            ]),
          ),
        ),
        of(false).pipe(
          map(() => startNewNoteInModal(contact, action.type == 'START_NEW_REMINDER')),
        ),
      );
    }),
  );

export const clearContactBatchOnSearchEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType(actionTypes.SET_SEARCH_TERM),
    switchMap(() =>
      // action$.ofType('FETCH_SEARCH_RESULTS_SUCCEEDED').pipe(take(1), map(clearContactBatch)),
      action$.pipe(ofType('FETCH_SEARCH_RESULTS_SUCCEEDED'), take(1), map(clearContactBatch)),
    ),
  );

export const saveOrResetSelectedStateEpic = (
  action$: Observable<ReduxAction>,
  state$: StateObservable<AppState>,
) =>
  action$.pipe(
    ofType('SET_LOCATION'),
    map(() => {
      const location = state$.value.ui.location;
      return {
        type: 'UPDATE_SELECTED_STATE_ON_NAVIGATE',
        payload: { to: location.pathname, from: location.lastPathname },
      };
    }),
  );

export const closeModalOnNavEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType('SET_LOCATION'),
    map(() => closeModal),
  );

const navBarTransitionDuration = 225;

const startNavBarTransitionEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType('TRANSITION_NAV_BAR'),
    throttleTime(navBarTransitionDuration),
    mergeMap(({ payload }) =>
      of(null).pipe(map(() => ({ type: 'NAV_BAR_START_TRANSITION', payload }))),
    ),
  );

const endNavBarTransitionEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType('NAV_BAR_START_TRANSITION'),
    delay(navBarTransitionDuration),
    mergeMap(() => of(null).pipe(map(() => ({ type: 'NAV_BAR_FINISHED_TRANSITION' })))),
  );

// FIXME figure out type issue
export const uiEpic = combineEpics(
  newNoteEpic,
  // @ts-ignore
  clearContactBatchOnSearchEpic,
  saveOrResetSelectedStateEpic,
  closeModalOnNavEpic,
  startNavBarTransitionEpic,
  endNavBarTransitionEpic,
);
